import React, { useState, useEffect, useCallback } from "react";
import { customerStatusLabel, isVipCustomer } from "../../utils/customerStatus";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { staffAPI, crmAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { useFocusedPoll } from "../../hooks/useFocusedPoll";
import BookingHistory from "./BookingHistory";
import StaffLayout from "./StaffLayout";
import StaffBookingForm from "./StaffBookingForm";
import CustomerEditModal from "./CustomerEditModal";
import CustomerPaymentManagement from "./CustomerPaymentManagement";
import CustomerVitalsStrip from "./CustomerVitalsStrip";
import EsignStatusCard from "./EsignStatusCard";
import IntroOfferStatusCard from "./IntroOfferStatusCard";
import BookingRestrictionsCard from "./BookingRestrictionsCard";
import BookingRestrictionsModal from "./BookingRestrictionsModal";
import SectionCard from "./SectionCard";
import CreditSummaryTable from "./CreditSummaryTable";
import CreditHistoryTable from "./CreditHistoryTable";
import {
  mergeCustomerRecord,
  hasPausableMembership,
} from "../../utils/customerProfileHelpers";
import "./CustomerProfile.css";

/**
 * CustomerProfile component for staff to view and manage detailed customer information
 * Displays personal info, membership details, booking history, and interactions.
 * (FEEDBACK-SOFT-HIDE-001: Inline Feedback tab removed.)
 */
const CustomerProfile = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [bookingStats, setBookingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  // FEEDBACK-SOFT-HIDE-001: feedback state + tab JSX removed from this page.
  //   Inline Feedback tab is no longer rendered. staffAPI.getCustomerFeedback
  //   remains in services/api.js (backend endpoint intact); we just stopped
  //   calling it from this admin profile. Testimonials.js continues to read
  //   the public feedback feed via feedbackAPI.getPublicFeedback.
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [interactionsPage, setInteractionsPage] = useState(1);
  const [totalInteractionsPages, setTotalInteractionsPages] = useState(1);
  const [communicationsPage, setCommunicationsPage] = useState(1);
  const [totalCommunicationsPages, setTotalCommunicationsPages] = useState(1);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [totalBookingsPages, setTotalBookingsPages] = useState(1);
  const [newTag, setNewTag] = useState("");
  const [tagColor, setTagColor] = useState("#4caf50");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRestrictionsModal, setShowRestrictionsModal] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [totalAuditPages, setTotalAuditPages] = useState(1);
  const [pauseStartDate, setPauseStartDate] = useState("");
  const [pauseEndDate, setPauseEndDate] = useState("");
  const [pauseReason, setPauseReason] = useState("");
  const [pauseSaving, setPauseSaving] = useState(false);
  const [pauseMessage, setPauseMessage] = useState("");
  // Credits by the display rule (GET /credit-summary): the credit table and
  // the Credit Balance card use this one source.
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditSummaryError, setCreditSummaryError] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("YYYY-MM-DD");
  }, []);

  const fetchCustomerData = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        const response = await staffAPI.getCustomerById(customerId);

        setCustomer(response.data);
        setPauseStartDate(
          formatDateForInput(response.data.membershipPauseStartsAt)
        );
        setPauseEndDate(
          formatDateForInput(response.data.membershipPauseEndsAt)
        );
        setPauseReason(response.data.membershipPauseReason || "");

        // BUG: staff customer-profile bookings tab silently flipped to N/A
        // every ~30s. The `useFocusedPoll(refreshCustomerHeader, 30000)` below
        // re-invokes this fetcher with { silent: true }, which (previously
        // unconditionally) overwrote the `bookings` state with the customer
        // DTO's `recentBookings` field. That field's shape is NOT the same as
        // `staffAPI.getCustomerBookings()` response — it is the raw SessionBooking
        // projection from `customerController.getCustomerById` (selects
        // `appointmentDate`, `createdAt`, `checkedInAt`, etc.; no `date`,
        // `suite`, `extras`, or nested `statusTimestamps` object). The
        // BookingHistory component renders `booking.date` and
        // `booking.statusTimestamps.{booked,checkedIn,started,completed,cancelled}`,
        // so on each silent poll the Date/Time column and Timeline timestamps
        // would go null on the live staff page (statistics persisted because
        // they live in separate `bookingStats` state).
        //
        // Fix: only seed `bookings` from the customer DTO on the NON-silent
        // initial-mount path (which is purely a paint-warm seed before the
        // bookings-tab `useEffect` runs `fetchBookings()` with the correctly-
        // shaped paginated endpoint). On the silent poll path, leave
        // `bookings` alone — the bookings-tab `useEffect` and the explicit
        // post-action handlers (line 708 below) own the correctly-shaped
        // refetch. Single-line scope change; revert is a one-commit revert.
        if (
          !silent &&
          response.data.recentBookings &&
          response.data.recentBookings.length > 0
        ) {
          setBookings(response.data.recentBookings);
        }

        if (!silent) setLoading(false);
      } catch (err) {
        handleApiError(
          err,
          "CustomerProfile",
          setError,
          "Error fetching customer data. Please try again."
        );
        if (!silent) setLoading(false);
      }
    },
    [customerId, formatDateForInput]
  );

  // BUG-104 fix: vitals strip "Lifetime Visits" must count completed
  // bookings only (spec §3.4). The customer DTO's `totalBookings` is
  // unfiltered. Pull the correctly-filtered count from the existing
  // booking-stats endpoint. Failures are non-fatal — strip falls back
  // to 0 visits, which is preferable to an inflated number.
  const fetchCreditSummary = useCallback(async () => {
    try {
      const response = await staffAPI.getCustomerCreditSummary(customerId);
      setCreditSummary(response.data || null);
      setCreditSummaryError(false);
    } catch (err) {
      setCreditSummaryError(true);
    }
  }, [customerId]);

  const fetchBookingStats = useCallback(async () => {
    if (!customerId) return;
    try {
      const response = await staffAPI.getCustomerBookingStats(customerId);
      setBookingStats(response.data?.stats || null);
    } catch (err) {
      // Silent failure: vitals strip will render "0 / no completed
      // visits" rather than the inflated unfiltered count.
      setBookingStats(null);
    }
  }, [customerId]);

  // Fetch customer data
  useEffect(() => {
    fetchCustomerData();
    fetchBookingStats();
    fetchCreditSummary();
  }, [fetchCustomerData, fetchBookingStats, fetchCreditSummary]);

  // Tier 1 sync posture: when staff returns to this tab, the window
  // regains focus, or the network reconnects, silently refetch the
  // customer header + booking stats so cross-tab edits (booking
  // changes, restriction edits, ESIGN status flips) propagate without
  // manual reload. The `silent: true` flag suppresses the loading
  // spinner — the page must NOT flicker on background refetch.
  const refreshCustomerHeader = useCallback(() => {
    fetchCustomerData({ silent: true });
    fetchBookingStats();
    fetchCreditSummary();
  }, [fetchCustomerData, fetchBookingStats, fetchCreditSummary]);
  useFocusedPoll(refreshCustomerHeader, 30000, [refreshCustomerHeader]);

  // Fetch customer bookings
  const fetchBookings = useCallback(async () => {
    try {
      setBookingsLoading(true);
      const response = await staffAPI.getCustomerBookings(customerId, {
        page: bookingsPage,
        limit: 5,
      });

      if (response.data && response.data.bookings) {
        setBookings(response.data.bookings);
        setTotalBookingsPages(response.data.pagination?.totalPages || 1);
      } else {
        setBookings([]);
      }

      setBookingsLoading(false);
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.bookings",
        setError,
        "Error fetching booking history."
      );
      setBookingsLoading(false);
    }
  }, [customerId, bookingsPage]);

  // Fetch customer interactions
  const fetchInteractions = useCallback(async () => {
    try {
      const response = await staffAPI.getCustomerInteractions(customerId, {
        page: interactionsPage,
        limit: 5,
      });

      setInteractions(response.data.interactions);
      setTotalInteractionsPages(response.data.pagination.totalPages);
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.interactions",
        setError,
        "Error fetching interaction history."
      );
    }
  }, [customerId, interactionsPage]);

  const fetchCommunications = useCallback(async () => {
    try {
      setCommunicationsLoading(true);
      const response = await staffAPI.getCustomerCommunications(customerId, {
        page: communicationsPage,
        limit: 10,
        type: "sms",
      });

      setCommunications(response.data.communications || []);
      setTotalCommunicationsPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.communications",
        setError,
        "Error fetching SMS history."
      );
    } finally {
      setCommunicationsLoading(false);
    }
  }, [communicationsPage, customerId]);

  // FEEDBACK-SOFT-HIDE-001: fetchFeedback callback removed along with the
  //   inline Feedback tab. staffAPI.getCustomerFeedback endpoint intact.

  // Fetch customer audit log
  const fetchAuditLog = useCallback(async () => {
    try {
      const response = await staffAPI.getCustomerAuditLog(customerId, {
        page: auditPage,
        limit: 10,
      });

      setAuditLog(response.data.auditLog || []);
      setTotalAuditPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.auditLog",
        setError,
        "Error fetching audit log."
      );
    }
  }, [customerId, auditPage]);

  // Fetch bookings when tab changes to bookings
  useEffect(() => {
    if (activeTab === "bookings") {
      fetchBookings();
    }
  }, [activeTab, bookingsPage, customerId, fetchBookings]);

  // Fetch interactions when tab changes to interactions
  useEffect(() => {
    if (activeTab === "interactions") {
      fetchInteractions();
    }
  }, [activeTab, interactionsPage, customerId, fetchInteractions]);

  useEffect(() => {
    if (activeTab === "communications") {
      fetchCommunications();
    }
  }, [activeTab, communicationsPage, customerId, fetchCommunications]);

  // FEEDBACK-SOFT-HIDE-001: feedback-tab useEffect removed.

  // Fetch audit log when tab changes to audit
  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditLog();
    }
  }, [activeTab, auditPage, customerId, fetchAuditLog]);

  // Handle adding a new note/interaction
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      setAddingNote(true);
      await crmAPI.addInteraction({
        userId: customerId,
        interactionType: "other",
        direction: "internal",
        subject: "Staff Note",
        notes: newNote,
        outcome: "information_provided",
      });

      // Refresh interactions if on interactions tab
      if (activeTab === "interactions") {
        fetchInteractions();
      }

      setNewNote("");
      setAddingNote(false);
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.addNote",
        setError,
        "Error adding note. Please try again."
      );
      setAddingNote(false);
    }
  };

  // Handle adding a new tag
  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;

    try {
      await staffAPI.addCustomerTag(customerId, {
        name: newTag,
        color: tagColor,
      });

      // Refresh customer data
      await fetchCustomerData();
      setNewTag("");
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.addTag",
        setError,
        "Error adding tag. Please try again."
      );
    }
  };

  // Handle removing a tag
  const handleRemoveTag = async (tagId) => {
    try {
      await staffAPI.removeCustomerTag(customerId, tagId);

      // Refresh customer data
      await fetchCustomerData();
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.removeTag",
        setError,
        "Error removing tag. Please try again."
      );
    }
  };

  // The status is calculated from visits; staff set only the VIP flag.
  const handleVipToggle = async () => {
    setStatusMenuOpen(false);
    const nextIsVip = !isVipCustomer(customer);
    try {
      const response = await staffAPI.updateCustomerStatus(customerId, {
        isVip: nextIsVip,
      });
      // Merge, do not replace: the response is the raw customer record and
      // would drop the computed fields (visits, spend, credits).
      setCustomer((prev) => ({
        ...prev,
        isVip: nextIsVip,
        customerStatus:
          response?.data?.customer?.customerStatus ?? prev.customerStatus,
      }));
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.updateStatus",
        setError,
        "Error updating customer status. Please try again."
      );
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment.utc(dateString).tz("America/Phoenix").format("MMM D, YYYY");
  };

  // Format date with time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return moment
      .utc(dateString)
      .tz("America/Phoenix")
      .format("MMM D, YYYY h:mm A");
  };

  const formatCommunicationCategory = (value) => {
    switch (value) {
      case "booking_confirmation":
        return "Booking Confirmation";
      case "booking_reminder":
        return "Booking Reminder";
      case "booking_cancellation":
        return "Booking Cancellation";
      default:
        return value
          ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
          : "Communication";
    }
  };

  // Format birthday to show only month and day
  const formatBirthday = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).utc().format("MMMM D");
  };

  const formatPauseStatus = (status) => {
    switch (status) {
      case "scheduled_pause":
        return "Scheduled Pause";
      case "paused":
        return "Paused";
      default:
        return "Active";
    }
  };

  const customerData = customer || {};
  const corporateProgramStatus = customerData.corporateProgramStatus || null;
  const recentCorporateBookings = (customerData.recentBookings || []).filter(
    (booking) =>
      booking.sessionSourceType === "organization-sponsored" ||
      booking.sessionSourceType === "organization-corporate-rate"
  );

  const getCorporateSessionStatusLabel = (booking) => {
    if (booking.sessionSourceType === "organization-sponsored") {
      return booking.sourceDescription || "Corporate Sponsored";
    }

    if (booking.sessionSourceType === "organization-corporate-rate") {
      return booking.sourceDescription || "Corporate Rate";
    }

    return booking.sourceDescription || "Corporate Session";
  };

  const getCorporateProgramSummaryLabel = () => {
    if (!corporateProgramStatus) {
      return "";
    }

    if (corporateProgramStatus.nextBookingSourceType === "organization-sponsored") {
      return "Next eligible session: Sponsored";
    }

    if (corporateProgramStatus.hasPersonalCreditsAvailable) {
      return "Next eligible session: Personal credits first";
    }

    return `Next eligible session: $${Number(
      corporateProgramStatus.corporateRate || 0
    ).toFixed(2)} corporate rate`;
  };

  const handleSavePause = async (pauseNow = false) => {
    if (!customer) return;

    try {
      setPauseSaving(true);
      setPauseMessage("");
      setError("");

      // "Today" in Phoenix, not the browser's date.
      const startDate = pauseNow
        ? moment().tz("America/Phoenix").format("YYYY-MM-DD")
        : pauseStartDate;

      if (!startDate) {
        setError("Pause start date is required.");
        setPauseSaving(false);
        return;
      }

      const payload = {
        membershipAccessStatus: pauseNow ? "paused" : "scheduled_pause",
        membershipPauseStartsAt: startDate,
        membershipPauseEndsAt: pauseEndDate || null,
        membershipPauseReason: pauseReason,
      };

      const response = await staffAPI.updateCustomerMembershipPause(
        customerId,
        payload
      );

      // Merge, do not replace: the response is the raw customer record and
      // would drop the computed fields (visits, spend, bookings).
      setCustomer((prev) => mergeCustomerRecord(prev, response.data.customer));
      fetchCreditSummary();
      setPauseStartDate(formatDateForInput(response.data.customer.membershipPauseStartsAt));
      setPauseEndDate(formatDateForInput(response.data.customer.membershipPauseEndsAt));
      setPauseReason(response.data.customer.membershipPauseReason || "");
      setPauseMessage(response.data.message || "Membership pause updated.");
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.updateMembershipPause",
        setError,
        "Error updating membership pause."
      );
    } finally {
      setPauseSaving(false);
    }
  };

  const handleResumeMembership = async () => {
    try {
      setPauseSaving(true);
      setPauseMessage("");
      setError("");

      const response = await staffAPI.resumeCustomerMembership(customerId);
      setCustomer((prev) => mergeCustomerRecord(prev, response.data.customer));
      fetchCreditSummary();
      setPauseStartDate("");
      setPauseEndDate("");
      setPauseReason("");
      setPauseMessage(response.data.message || "Membership access resumed.");
    } catch (err) {
      handleApiError(
        err,
        "CustomerProfile.resumeMembership",
        setError,
        "Error resuming membership access."
      );
    } finally {
      setPauseSaving(false);
    }
  };

  // Navigate back to customer list
  const handleBack = () => {
    navigate(location.state?.returnTo || "/staff/customers");
  };

  // Render loading state
  if (loading) {
    return <div className="loading">Loading customer profile...</div>;
  }

  // Render error state
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Handle booking success
  const handleBookingSuccess = (booking) => {
    setShowBookingForm(false);
    // Refresh bookings if on bookings tab
    if (activeTab === "bookings") {
      fetchBookings();
    }
  };

  // Render booking form modal
  const renderBookingForm = () => {
    if (!showBookingForm) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content staff-booking-modal">
          <button
            className="modal-close-btn"
            onClick={() => setShowBookingForm(false)}
          >
            &times;
          </button>
          <StaffBookingForm
            customerId={customerId}
            onSuccess={handleBookingSuccess}
            onCancel={() => setShowBookingForm(false)}
          />
        </div>
      </div>
    );
  };

  // Render if customer not found
  if (!customer) {
    return <div className="error-message">Customer not found.</div>;
  }

  return (
    <StaffLayout>
      <div className="customer-profile-container">
        <button className="back-button back-button--above-header" onClick={handleBack}>
          &larr; Back to Customers
        </button>
        <div className="customer-profile-header">
          <div className="customer-header-content">
            {customer.profilePicture && (
              <div className="profile-picture-container">
                <img
                  src={customer.profilePicture}
                  alt={`${customer.firstName} ${customer.lastName}`}
                  className="profile-picture"
                />
              </div>
            )}
            <div className="customer-header-info">
              <h1>
                {customer.firstName} {customer.lastName}
                {customer.preferredName && ` (${customer.preferredName})`}
              </h1>
              {/*
                CUST-WW-BADGE-001 — Wellness Wednesday header chip.
                Strict trigger matches CustomerList row: remaining > 0.
                See CustomerList.js colorMap for the WW token + reasoning.
              */}
              {customer.wellnessWednesdayCredits?.remaining > 0 && (
                <div className="customer-header-chips">
                  <span
                    className="product-badge product-badge--header"
                    title="Wellness Wednesday"
                    aria-label="Wellness Wednesday"
                    style={{ backgroundColor: "#00838F", color: "white" }}
                  >
                    WW
                  </span>
                </div>
              )}
              {customer.birthday && (
                <div className="customer-birthday">
                  <span>Birthday: {formatBirthday(customer.birthday)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="customer-status-container" data-testid="customer-status">
            <span
              className={`status-badge status-${customer.computedStatus || "new"} active`}
              title="Calculated from completed visits"
            >
              {customerStatusLabel(customer.computedStatus)}
            </span>
            {isVipCustomer(customer) && (
              <span className="status-badge status-vip active">VIP</span>
            )}
            <div className="status-menu">
              <button
                type="button"
                className="status-menu__toggle"
                aria-haspopup="menu"
                aria-expanded={statusMenuOpen}
                aria-label="Change status"
                onClick={() => setStatusMenuOpen((open) => !open)}
              >
                &#8943;
              </button>
              {statusMenuOpen && (
                <div className="status-menu__list" role="menu">
                  <button type="button" role="menuitem" onClick={handleVipToggle}>
                    {isVipCustomer(customer) ? "Remove VIP" : "Mark as VIP"}
                  </button>
                  <p className="status-menu__note">
                    New, Active, At Risk, and Inactive are calculated from visits.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CUST-DETAIL-POLISH-002 — at-a-glance vitals strip */}
        <CustomerVitalsStrip
          customer={customer}
          creditSummary={creditSummary}
          bookingStats={bookingStats}
          loading={loading}
          error={Boolean(error) && !customer}
          onActivateTab={setActiveTab}
        />

        {/* ESIGN-UX-REDESIGN-001 (Story 2 Surface 3) — ESIGN status card.
            Sits between vitals strip and tabs per Zuri spec §5.1.
            Reads `esignStatus` block from /api/staff/customers/:id; renders
            null gracefully if backend has not yet deployed the additive
            contract. Privacy guardrail (§5.4): NO PDF / NO signature image
            / NO IP — DO NOT REGRESS. */}
        <EsignStatusCard
          esignStatus={customer?.esignStatus}
          loading={loading && !customer}
          error={Boolean(error) && Boolean(customer) && !customer?.esignStatus}
        />

        {/* INTRO-OFFER-001-E — admin intro-offer status card.
            Sits below ESIGN status card per Zuri spec §4.1 (between the
            ESIGN status card and the tabs, mirroring the stacked-status-row
            pattern Sprint 6 established).
            Reads `introOffer` (additive sub-doc) + `hasFreeFlexible` (legacy
            top-level flag) from /api/staff/customers/:customerId; gracefully
            renders nothing if BOTH are absent (Scenario 12 — page does not
            shift layout). The card has NO CTAs that intervene through the
            card (no claim-on-behalf / extend / refund) per spec §4.4 +
            Scenario 8. */}
        <IntroOfferStatusCard
          introOffer={customer?.introOffer}
          hasFreeFlexible={Boolean(customer?.hasFreeFlexible)}
          loading={loading && !customer}
          error={
            Boolean(error) &&
            Boolean(customer) &&
            !customer?.introOffer &&
            !customer?.hasFreeFlexible
          }
        />

        {/* BOOKING-RESTRICT-003 — Booking Restrictions card (Surface A).
            Sits below ESIGN status card per Zuri spec §3.1.
            Hard Rule §: STAFF-ONLY presence — this card and its modal must
            never be imported into customer-facing components. */}
        <BookingRestrictionsCard
          restrictionsSummary={customer?.restrictionsSummary}
          bookingRestrictions={customer?.bookingRestrictions}
          loading={loading && !customer}
          error={Boolean(error) && Boolean(customer) && customer?.restrictionsSummary === undefined}
          onOpenManage={() => setShowRestrictionsModal(true)}
        />

        {/* Profile Tabs */}
        <div className="profile-tabs">
          <button
            className={activeTab === "overview" ? "active" : ""}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={activeTab === "memberships" ? "active" : ""}
            onClick={() => setActiveTab("memberships")}
          >
            Session Credits
          </button>
          <button
            className={activeTab === "bookings" ? "active" : ""}
            onClick={() => setActiveTab("bookings")}
          >
            Bookings
          </button>
          <button
            className={activeTab === "payments" ? "active" : ""}
            onClick={() => setActiveTab("payments")}
          >
            Payment Methods
          </button>
          <button
            className={activeTab === "interactions" ? "active" : ""}
            onClick={() => setActiveTab("interactions")}
          >
            Interactions
          </button>
          <button
            className={activeTab === "communications" ? "active" : ""}
            onClick={() => setActiveTab("communications")}
          >
            SMS History
          </button>
          {/* FEEDBACK-SOFT-HIDE-001: Inline "Feedback" tab button removed. */}
          <button
            className={activeTab === "audit" ? "active" : ""}
            onClick={() => setActiveTab("audit")}
          >
            Audit Log
          </button>
        </div>

        {/* Quick Actions Bar */}
        <div className="quick-actions">
          <button
            className="edit-profile-btn"
            onClick={() => setShowEditModal(true)}
          >
            Edit Profile
          </button>

          <button
            className="quick-note-btn"
            onClick={() => {
              setActiveTab("interactions");
              // Use setTimeout to ensure the DOM has updated before trying to focus
              setTimeout(() => {
                const textarea = document.querySelector(
                  ".add-note-form textarea"
                );
                if (textarea) {
                  textarea.focus();
                }
              }, 100);
            }}
          >
            Add Note
          </button>

          <button
            className="book-session-btn"
            onClick={() => setShowBookingForm(true)}
          >
            Book Session
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="overview-content customer-detail-overview-grid">
            {/* CUST-DETAIL-POLISH-003 — Personal Information section card */}
            <SectionCard
              heading="Personal Information"
              headingId="customer-section-personal-info"
              accent="gold"
            >
              <div className="profile-grid">
                <div className="profile-item">
                  <label>Email</label>
                  <p>{customer.email}</p>
                </div>
                <div className="profile-item">
                  <label>Phone</label>
                  <p>{customer.contactNumber || "Not provided"}</p>
                </div>
                <div className="profile-item">
                  <label>Customer Since</label>
                  <p>{formatDate(customer.createdAt)}</p>
                </div>
              </div>
            </SectionCard>

            {/* Membership Pause: only when the customer has a membership.
                Active: Schedule Pause and Pause Now. Paused or scheduled:
                Resume Membership only. (Pause behavior gaps: issue #270.) */}
            {hasPausableMembership(customer, creditSummary) && (
              <SectionCard
                heading="Membership Pause"
                headingId="customer-section-membership-pause"
                accent="teal"
                className="pause-section-card"
              >
                <p className="pause-card__note">
                  Changes booking access in the app only. Update Stripe separately.
                </p>
                {["paused", "scheduled_pause"].includes(
                  customer.membershipAccessStatus
                ) ? (
                  <div className="pause-card" data-testid="pause-card-paused">
                    <dl className="pause-card__facts">
                      <dt>Status</dt>
                      <dd>{formatPauseStatus(customer.membershipAccessStatus)}</dd>
                      <dt>Pause start</dt>
                      <dd>
                        {customer.membershipPauseStartsAt
                          ? formatDate(customer.membershipPauseStartsAt)
                          : "Not set"}
                      </dd>
                      <dt>Pause end</dt>
                      <dd>
                        {customer.membershipPauseEndsAt
                          ? formatDate(customer.membershipPauseEndsAt)
                          : "Not set"}
                      </dd>
                      {customer.membershipPauseReason && (
                        <>
                          <dt>Reason</dt>
                          <dd>{customer.membershipPauseReason}</dd>
                        </>
                      )}
                    </dl>
                    <div className="pause-action-row">
                      <button
                        type="button"
                        className="action-btn action-btn-success"
                        onClick={handleResumeMembership}
                        disabled={pauseSaving}
                      >
                        {pauseSaving ? "Saving..." : "Resume Membership"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pause-controls" data-testid="pause-card-active">
                    <div className="pause-form-grid">
                      <div className="pause-form-field">
                        <label htmlFor="pauseStartDate">Pause start date</label>
                        <input
                          id="pauseStartDate"
                          type="date"
                          value={pauseStartDate}
                          onChange={(e) => setPauseStartDate(e.target.value)}
                        />
                      </div>
                      <div className="pause-form-field">
                        <label htmlFor="pauseEndDate">Pause end date</label>
                        <input
                          id="pauseEndDate"
                          type="date"
                          value={pauseEndDate}
                          onChange={(e) => setPauseEndDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="pause-form-field">
                      <label htmlFor="pauseReason">Pause reason</label>
                      <textarea
                        id="pauseReason"
                        value={pauseReason}
                        onChange={(e) => setPauseReason(e.target.value)}
                        placeholder="Optional note for staff"
                        rows="3"
                      />
                    </div>
                    <div className="pause-action-row">
                      <button
                        type="button"
                        className="action-btn action-btn-warning"
                        onClick={() => handleSavePause(false)}
                        disabled={pauseSaving}
                      >
                        {pauseSaving ? "Saving..." : "Schedule Pause"}
                      </button>
                      <button
                        type="button"
                        className="action-btn action-btn-danger"
                        onClick={() => handleSavePause(true)}
                        disabled={pauseSaving}
                      >
                        {pauseSaving ? "Saving..." : "Pause Now"}
                      </button>
                    </div>
                  </div>
                )}
                {pauseMessage && (
                  <div className="pause-message success-message">{pauseMessage}</div>
                )}
              </SectionCard>
            )}

            {(corporateProgramStatus || recentCorporateBookings.length > 0) && (
              <SectionCard
                heading="Corporate Program Status"
                headingId="customer-section-corporate-program"
                accent="default"
              >

                {corporateProgramStatus ? (
                  <div className="credit-summary corporate-program">
                    <div className="credit-summary-header">
                      <span>
                        {corporateProgramStatus.organizationCode} •{" "}
                        {corporateProgramStatus.programName}
                      </span>
                    </div>
                    <div className="credit-summary-details">
                      <p>
                        <strong>Organization:</strong>{" "}
                        {corporateProgramStatus.organizationName}
                      </p>
                      <p>
                        <strong>Current Month:</strong>{" "}
                        {corporateProgramStatus.yearMonth}
                      </p>
                      <p>
                        <strong>Sponsored Used:</strong>{" "}
                        {corporateProgramStatus.employeeSponsoredUsed || 0} /{" "}
                        {corporateProgramStatus.sponsoredSessionsPerEmployeePerMonth || 0}
                      </p>
                      <p>
                        <strong>Corporate-Rate Sessions:</strong>{" "}
                        {corporateProgramStatus.employeeCorporateRateUsed || 0}
                      </p>
                      <p>
                        <strong>Program Summary:</strong>{" "}
                        {getCorporateProgramSummaryLabel()}
                      </p>
                      <p>{corporateProgramStatus.summaryMessage}</p>
                    </div>
                  </div>
                ) : null}

                {recentCorporateBookings.length > 0 ? (
                  <div className="corporate-booking-status-list">
                    <h3>Recent Corporate-Funded Sessions</h3>
                    {recentCorporateBookings.slice(0, 5).map((booking) => (
                      <div
                        className="corporate-booking-status-item"
                        key={booking._id}
                      >
                        <div className="corporate-booking-status-copy">
                          <strong>{formatDateTime(booking.appointmentDate)}</strong>
                          <span>
                            {getCorporateSessionStatusLabel(booking)}
                            {booking.sessionSourceType ===
                            "organization-corporate-rate"
                              ? ` • $${Number(
                                  booking.organizationRateApplied || 0
                                ).toFixed(2)}`
                              : ""}
                          </span>
                        </div>
                        <span
                          className={`corporate-session-badge ${
                            booking.sessionSourceType === "organization-sponsored"
                              ? "sponsored"
                              : "corporate-rate"
                          }`}
                        >
                          {booking.sessionSourceType === "organization-sponsored"
                            ? "Sponsored"
                            : "Corporate Rate"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="corporate-status-empty">
                    No recent corporate-funded sessions yet.
                  </p>
                )}
              </SectionCard>
            )}

            {/* Session Credit Summary: what staff can book for this customer
                now (server display rule; completed counts are on the
                Session Credits tab). */}
            <SectionCard
              heading="Session Credit Summary"
              headingId="customer-section-credit-summary"
              accent="default"
              fullWidth
            >
              <CreditSummaryTable
                summary={creditSummary}
                loading={!creditSummary && !creditSummaryError}
                error={creditSummaryError}
              />
            </SectionCard>

            {/* CUST-DETAIL-POLISH-003 — Customer Tags section card */}
            <SectionCard
              heading="Customer Tags"
              headingId="customer-section-tags"
              accent="teal"
            >
              <div className="customer-tags-container-compact">
                <div className="tags-list-compact">
                  {customer.tags && customer.tags.length > 0 ? (
                    customer.tags.map((tag) => (
                      <div
                        key={tag._id || tag.id}
                        className="customer-tag"
                        style={{ backgroundColor: tag.color || "#4caf50" }}
                      >
                        {tag.name}
                        <button
                          className="remove-tag-btn"
                          onClick={() => handleRemoveTag(tag._id || tag.id)}
                        >
                          &times;
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="no-tags">No tags added yet</div>
                  )}
                </div>
                <form className="add-tag-form-compact" onSubmit={handleAddTag}>
                  <input
                    type="text"
                    placeholder="Add a tag..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                  />
                  <input
                    type="color"
                    value={tagColor}
                    onChange={(e) => setTagColor(e.target.value)}
                  />
                  <button type="submit">Add</button>
                </form>
              </div>
            </SectionCard>
          </div>
        )}

        {activeTab === "memberships" && (
          <div className="memberships-content customer-detail-overview-grid">
            <SectionCard
              heading="Credits staff can book now"
              headingId="customer-section-credits-now"
              fullWidth
            >
              <CreditSummaryTable
                summary={creditSummary}
                loading={!creditSummary && !creditSummaryError}
                error={creditSummaryError}
              />
            </SectionCard>
            <SectionCard
              heading="Credit history"
              headingId="customer-section-credit-history"
              fullWidth
            >
              <p className="credit-history__intro">
                Every credit on the account, including used-up packages,
                expired credits, and cancelled memberships. Booked is the
                stored counter; Completed (period) resets each billing period.
              </p>
              <CreditHistoryTable customer={customer} />
            </SectionCard>
          </div>
        )}

        {activeTab === "bookings" && (
          <div className="bookings-content">
            {bookingsLoading ? (
              <div className="loading">Loading bookings...</div>
            ) : (
              <>
                <BookingHistory
                  bookings={bookings}
                  formatDateTime={formatDateTime}
                  customerId={customer._id}
                />
                {bookings.length > 0 && totalBookingsPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() =>
                        setBookingsPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={bookingsPage === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {bookingsPage} of {totalBookingsPages}
                    </span>
                    <button
                      onClick={() =>
                        setBookingsPage((prev) =>
                          Math.min(prev + 1, totalBookingsPages)
                        )
                      }
                      disabled={bookingsPage === totalBookingsPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "payments" && (
          <div className="payments-content">
            <CustomerPaymentManagement
              customerId={customerId}
              customer={customer}
            />
          </div>
        )}

        {activeTab === "interactions" && (
          <div className="interactions-content">
            <div className="add-note-form">
              <h3>Add a Note</h3>
              <form onSubmit={handleAddNote}>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this customer..."
                  required
                ></textarea>
                <button type="submit" disabled={addingNote}>
                  {addingNote ? "Adding..." : "Add Note"}
                </button>
              </form>
            </div>

            <div className="interactions-list">
              {interactions.length > 0 ? (
                interactions.map((interaction) => (
                  <div
                    key={interaction._id || interaction.id}
                    className="interaction-card"
                  >
                    <div className="interaction-header">
                      <div className="interaction-type">
                        {interaction.interactionType || "Note"}
                      </div>
                      <div className="interaction-date">
                        {formatDateTime(interaction.createdAt)}
                      </div>
                    </div>
                    <p>{interaction.notes}</p>
                    <div className="interaction-footer">
                      <span>
                        By:{" "}
                        {interaction.staffName ||
                          interaction.createdBy ||
                          "Staff"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p>No interactions found.</p>
              )}
            </div>

            {interactions.length > 0 && totalInteractionsPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    setInteractionsPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={interactionsPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {interactionsPage} of {totalInteractionsPages}
                </span>
                <button
                  onClick={() =>
                    setInteractionsPage((prev) =>
                      Math.min(prev + 1, totalInteractionsPages)
                    )
                  }
                  disabled={interactionsPage === totalInteractionsPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === "communications" && (
          <div className="communications-content">
            {communicationsLoading ? (
              <div className="loading">Loading SMS history...</div>
            ) : communications.length > 0 ? (
              <>
                <div className="communications-list">
                  {communications.map((entry) => (
                    <div
                      key={entry._id || entry.id}
                      className="communication-card"
                    >
                      <div className="communication-header">
                        <div className="communication-type">
                          {formatCommunicationCategory(entry.category)}
                        </div>
                        <div
                          className={`communication-status status-${entry.status}`}
                        >
                          {entry.status || "sent"}
                        </div>
                      </div>
                      <div className="communication-date">
                        {formatDateTime(entry.sentAt || entry.createdAt)}
                      </div>
                      <div className="communication-meta-grid">
                        <div>
                          <label>Recipient</label>
                          <p>{entry.metadata?.recipient || customer.contactNumber || "N/A"}</p>
                        </div>
                        <div>
                          <label>Provider</label>
                          <p>{entry.metadata?.providerName || "mailchimp_transactional_sms"}</p>
                        </div>
                        <div>
                          <label>Provider Status</label>
                          <p>{entry.metadata?.providerStatus || entry.status || "N/A"}</p>
                        </div>
                        <div>
                          <label>Reminder Type</label>
                          <p>{entry.metadata?.reminderType || "N/A"}</p>
                        </div>
                        <div>
                          <label>Session Type</label>
                          <p>{entry.metadata?.sessionType || "N/A"}</p>
                        </div>
                        <div>
                          <label>Message ID</label>
                          <p>{entry.metadata?.providerMessageId || "N/A"}</p>
                        </div>
                      </div>
                      {entry.metadata?.error ? (
                        <div className="communication-error">
                          <strong>Error:</strong> {entry.metadata.error}
                        </div>
                      ) : null}
                      <div className="communication-content">
                        <label>Message</label>
                        <p>{entry.content}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {totalCommunicationsPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() =>
                        setCommunicationsPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={communicationsPage === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {communicationsPage} of {totalCommunicationsPages}
                    </span>
                    <button
                      onClick={() =>
                        setCommunicationsPage((prev) =>
                          Math.min(prev + 1, totalCommunicationsPages)
                        )
                      }
                      disabled={communicationsPage === totalCommunicationsPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p>No SMS history found.</p>
            )}
          </div>
        )}

        {/* FEEDBACK-SOFT-HIDE-001: Inline Feedback tab content removed
            (state, fetch, list-render, pagination). staffAPI.getCustomerFeedback
            remains exported from services/api.js; backend endpoint untouched. */}

        {activeTab === "audit" && (
          <div className="audit-content">
            {auditLog.length > 0 ? (
              <>
                <div className="audit-list">
                  {auditLog.map((entry) => (
                    <div key={entry._id || entry.id} className="audit-card">
                      <div className="audit-header">
                        <div className="audit-action">
                          {entry.action || "Action"}
                        </div>
                        <div className="audit-date">
                          {formatDateTime(entry.createdAt)}
                        </div>
                      </div>
                      <div className="audit-details">
                        <p>
                          <strong>Field:</strong> {entry.field || "N/A"}
                        </p>
                        <p>
                          <strong>Old Value:</strong> {entry.oldValue || "N/A"}
                        </p>
                        <p>
                          <strong>New Value:</strong> {entry.newValue || "N/A"}
                        </p>
                      </div>
                      <div className="audit-footer">
                        <span>
                          By: {entry.staffName || entry.createdBy || "Staff"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {totalAuditPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() =>
                        setAuditPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={auditPage === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {auditPage} of {totalAuditPages}
                    </span>
                    <button
                      onClick={() =>
                        setAuditPage((prev) =>
                          Math.min(prev + 1, totalAuditPages)
                        )
                      }
                      disabled={auditPage === totalAuditPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p>No audit log entries found.</p>
            )}
          </div>
        )}

        {renderBookingForm()}

        {/* Edit Modal */}
        {showEditModal && (
          <CustomerEditModal
            isOpen={showEditModal}
            customer={customer}
            onClose={() => setShowEditModal(false)}
            onSuccess={(updatedCustomer) => {
              setCustomer(updatedCustomer);
              setShowEditModal(false);
            }}
          />
        )}

        {/* BOOKING-RESTRICT-003 — Surface B (admin block-list management).
            Hard Rule §: STAFF-ONLY mount; never imported on customer pages. */}
        {showRestrictionsModal && (
          <BookingRestrictionsModal
            isOpen={showRestrictionsModal}
            customerId={customerId}
            customer={customer}
            bookingRestrictions={customer?.bookingRestrictions}
            onClose={() => setShowRestrictionsModal(false)}
            onSaved={() => {
              fetchCustomerData();
            }}
          />
        )}
      </div>
    </StaffLayout>
  );
};

export default CustomerProfile;
